import React from "react";
import styled from "styled-components";
import Window from "../Window";
import PixelIcon from "../PixelIcon";
import MenuBar from "../MenuBar";
import Board from "../Board";
import WinCelebration from "../WinCelebration";
import { wasteVisibleCount, isStockDeadEnd } from "../../engine/solitaire";

// Shared chrome for both PlayableSolitaire and SpectatedSolitaire: Win95
// window + menu bar + board + score area. Pure render — caller owns state
// and handlers. No open animation lives here: a window either appears
// plainly or the Stage draws its wireframe arrival.
//
// Positioning is explicitly NOT owned here. Callers wrap this in whatever
// positioning container they want (centered for the player's own game,
// grid-slot for neighbors).

const DEFAULT_CARD_BACK = "back-3";
const noop = () => {};

// The felt's natural box inside the 620x435 window (measured; the
// window overhead is 10px wide — 3px .window padding + 2px body margin
// per side — and ~71px tall: title bar, menu bar, status bar, padding).
// boardScale shrinks THIS box only; chrome renders at full size around it.
const FELT_W = 610;
const FELT_H = 364;
const CHROME_W = 620 - FELT_W;
const CHROME_H = 435 - FELT_H;

// Outer window dimensions for a given board scale — exported so
// callers (drag boxes, layout math) can size the container without
// duplicating the chrome arithmetic.
export function scaledWindowSize(boardScale) {
  return {
    width: Math.round(FELT_W * boardScale) + CHROME_W,
    height: Math.round(FELT_H * boardScale) + CHROME_H,
  };
}

function SolitaireCore({
  title,
  state,
  selection = null,
  // Titlebar icon; must match the window's taskbar icon.
  titleIcon = "solitaire",
  cardBack = DEFAULT_CARD_BACK,
  controls = ["minimize", "maximize", "close"],
  disabledControls,
  pressedControls,
  onControlClick,
  interactive = true,
  // Real desktop focus: the Stage decides which ONE window is active.
  // Distinct from `interactive` (can you play this board) — your own
  // window greys out too when a neighbor has focus.
  inactive = false,
  onDrawClick = noop,
  onWasteClick = noop,
  onFoundationClick = noop,
  onTableauCardClick = noop,
  onTableauEmptyClick = noop,
  // { foundations: string[][] (sprite names), onPopTop: (i) => void }.
  // When set, the cascading-cards canvas overlays the window body and pops
  // cards off the caller's celebration state as they launch.
  winCelebration = null,
  // MenuBar items; PlayableSolitaire passes a real Game menu, spectated
  // windows keep the decorative defaults.
  menus = ["Game", "Help"],
  // Scale the GAME SURFACE only — chrome stays full-size. Currently
  // safe only for static, non-interactive windows (the past-game
  // viewer): remote cursors, the close ritual, and the cascade overlay
  // all assume the uniform 620x435 window space, so spectated windows
  // need a coordinate remap before they can use this (REVISIT.md).
  boardScale = 1,
  className,
}) {
  const foundationCellRefs = React.useRef([]);
  // The cascade canvas measures itself at mount; give the window a tick
  // to be laid out before the overlay measures, so a show joined
  // mid-flight doesn't read a 0px rect and play invisibly.
  const hasCelebration = winCelebration != null;
  const [overlayReady, setOverlayReady] = React.useState(false);
  React.useEffect(() => {
    if (!hasCelebration) {
      setOverlayReady(false);
      return undefined;
    }
    const t = setTimeout(() => setOverlayReady(true), 50);
    return () => clearTimeout(t);
  }, [hasCelebration]);
  // Cards-to-foundations is the one stat the state can vouch for. During
  // a win celebration `state` is the popped-down display copy the
  // cascade drains — the real count is a won game's 52, not the drained 0.
  const cardsUp = hasCelebration
    ? 52
    : state.foundations.reduce((n, f) => n + f.length, 0);
  const scaled = boardScale !== 1;
  const frame = scaled ? scaledWindowSize(boardScale) : null;
  const board = (
    <Board
      state={state}
      selection={selection}
      cardBack={cardBack}
      interactive={interactive}
      wasteVisibleCount={wasteVisibleCount(state)}
      stockDeadEnd={isStockDeadEnd(state)}
      onDrawClick={onDrawClick}
      onWasteClick={onWasteClick}
      onFoundationClick={onFoundationClick}
      onTableauCardClick={onTableauCardClick}
      onTableauEmptyClick={onTableauEmptyClick}
      foundationCellRefs={foundationCellRefs}
    />
  );
  return (
    <WindowFrame
      className={className}
      style={
        scaled
          ? { "--frame-w": frame.width + "px", "--frame-h": frame.height + "px" }
          : undefined
      }
    >
      <StyledWindow
        title={title}
        icon={<PixelIcon name={titleIcon} size={16} />}
        controls={controls}
        disabledControls={disabledControls}
        pressedControls={pressedControls}
        onControlClick={onControlClick}
        bodyStyle={{ margin: "0px 2px 0px" }}
        inactive={inactive}
        statusFields={[`Cards: ${cardsUp} / 52`]}
      >
        <Content $interactive={interactive}>
          <MenuBar items={menus} />
          <FeltArea>
            {scaled ? (
              // Natural-size felt stage, transform-scaled: sprites stay
              // integer-designed and the board layout never learns about
              // the scale.
              <FeltStage style={{ "--board-scale": boardScale }}>
                {board}
              </FeltStage>
            ) : (
              board
            )}
            {winCelebration && overlayReady && (
              <WinCelebration
                active
                foundations={winCelebration.foundations}
                foundationRefs={foundationCellRefs}
                seed={winCelebration.seed}
                startedAtMs={winCelebration.startedAtMs}
                onPopTop={winCelebration.onPopTop}
                onDone={winCelebration.onDone}
              />
            )}
          </FeltArea>
        </Content>
      </StyledWindow>
    </WindowFrame>
  );
}

const WindowFrame = styled.div`
  width: var(--frame-w, 620px);
  height: var(--frame-h, 435px);
`;

// Fixed natural-size stage for a scaled board: the Board lays out at
// full size and the transform does the shrinking, exactly like the
// whole-window ScaleBox — but inside full-size chrome.
const FeltStage = styled.div`
  width: ${FELT_W}px;
  height: ${FELT_H}px;
  transform: scale(var(--board-scale));
  transform-origin: top left;
`;

const StyledWindow = styled(Window)`
  width: 100%;
  height: 100%;
`;

// The felt and ONLY the felt: the cascade canvas anchors here, so
// bounced cards clip at the felt's top edge instead of drawing over
// the menu bar (the original clipped to the client area too).
const FeltArea = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  /* Lock MenuBar (first child) at its natural height. Without this, a
     tableau column with lots of stacked cards grows Board tall enough to
     push total children > container, and both flex items shrink — visibly
     squishing the bar. Board absorbs any excess instead. (The status bar
     now lives in Window chrome, outside this flex column.) */
  & > :first-child {
    flex-shrink: 0;
  }
  /* Non-interactive mode (spectating): suppress clicks on the board + score
     area but keep the menubar clickable for any future chrome-level UI. */
  ${(props) =>
    !props.$interactive &&
    `
    & > *:not(:first-child) {
      pointer-events: none;
    }
  `}
`;

export default SolitaireCore;
